import useInvalidateQuery from "@/composables/useInvalidateQueryData";
import type { CountryCode } from "@/interfaces/countryCode";
import axios from "@/lib/axios";
import { useCountryCodesStore } from "@/stores/countryCodes";
import { useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { MaybeRef, watch } from "vue";
import { onBeforeRouteLeave } from "vue-router";

interface Props {
  params?: Params;
  immediate?: boolean;
}

interface Params {
  includes?: string[];
  ids?: MaybeRef<Array<number | string> | undefined>;
}

const getCountryCodes = async (params?: Params): Promise<CountryCode[]> => {
  const { data } = await axios.get<CountryCode[]>(`/api/countrycodes`, {
    params: {
      ...params,
      ids: get(params?.ids),
    },
  });
  return data;
};

const useCountryCodes = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const { invalidateQuery } = useInvalidateQuery();
  const { countryCodes } = storeToRefs(useCountryCodesStore());
  const { setCountryCodes, resetState } = useCountryCodesStore();

  onBeforeRouteLeave(() => {
    resetState();
    invalidateQuery(["countryCodes"]);
  });

  const { data, isError, isLoading, isFetching, refetch } = useQuery(
    ["countryCodes"],
    () => getCountryCodes(params),
    {
      retry: false,
      enabled: immediate,
    },
  );

  const searchCountryCodes = async () => {
    await refetch();
  };

  watch(
    data,
    (response) => {
      if (response) {
        setCountryCodes(response);
      }
    },
    { immediate: true },
  );

  return {
    isError,
    isFetching,
    isLoading,
    countryCodes,
    data,

    refetch,
    searchCountryCodes,
    resetState,
  };
};

export default useCountryCodes;
